/**
 * FilesFilterBar — TASK-818.
 *
 * The Files tab's only filter was a text box: to see one client's documents you had to know
 * their name and type it. These are the same filters as selections — the dropdowns carry the
 * clients and formats that actually exist in the data, so every option leads somewhere.
 *
 * The search box stays, deliberately demoted: it is for finding a document by name inside a
 * long list, not the way you narrow to a client.
 */

import React from 'react';
import { Plus, Search, X } from 'lucide-react';

import {
  ALL,
  EMPTY_FILTERS,
  type ClientOption,
  type FileFilters,
  type FileKind,
} from '../../../utils/groupClientFiles';

interface FilesFilterBarProps {
  filters: FileFilters;
  onChange: (next: FileFilters) => void;
  clients: ClientOption[];
  formats: string[];
  onCreate: () => void;
  createDisabled?: boolean;
}

const SELECT_CLASS =
  'rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-800 ' +
  'focus:border-[#002B45] focus:outline-none focus:ring-1 focus:ring-[#002B45]';

const KINDS: { value: FileKind; label: string }[] = [
  { value: ALL, label: 'Everything' },
  { value: 'documents', label: 'Working documents' },
  { value: 'deliverables', label: 'Deliverables' },
];

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500">
    {label}
    {children}
  </label>
);

export const FilesFilterBar: React.FC<FilesFilterBarProps> = ({
  filters,
  onChange,
  clients,
  formats,
  onCreate,
  createDisabled,
}) => {
  const set = (patch: Partial<FileFilters>) => onChange({ ...filters, ...patch });
  const isFiltered =
    filters.client !== ALL ||
    filters.format !== ALL ||
    filters.kind !== ALL ||
    filters.search !== '';

  return (
    <div
      className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-gray-200 bg-white px-4 py-3"
      data-testid="files-filter-bar"
    >
      <Field label="Client">
        <select
          value={filters.client}
          onChange={(e) => set({ client: e.target.value })}
          className={SELECT_CLASS}
          data-testid="filter-client"
        >
          <option value={ALL}>All clients</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name} ({c.count})
            </option>
          ))}
        </select>
      </Field>

      <Field label="Type">
        <select
          value={filters.format}
          onChange={(e) => set({ format: e.target.value })}
          className={SELECT_CLASS}
          data-testid="filter-format"
        >
          <option value={ALL}>All types</option>
          {formats.map((f) => (
            <option key={f} value={f}>
              {f.toUpperCase()}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Show">
        <select
          value={filters.kind}
          onChange={(e) => set({ kind: e.target.value as FileKind })}
          className={SELECT_CLASS}
          data-testid="filter-kind"
        >
          {KINDS.map((k) => (
            <option key={k.value} value={k.value}>
              {k.label}
            </option>
          ))}
        </select>
      </Field>

      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={filters.search}
          onChange={(e) => set({ search: e.target.value })}
          placeholder="Search by name"
          aria-label="Search documents by name"
          data-testid="filter-search"
          className="w-48 rounded-md border border-gray-300 py-1.5 pl-8 pr-2.5 text-sm focus:border-[#002B45] focus:outline-none focus:ring-1 focus:ring-[#002B45]"
        />
      </div>

      {isFiltered && (
        <button
          type="button"
          onClick={() => onChange({ ...EMPTY_FILTERS })}
          data-testid="filter-clear"
          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-700"
        >
          <X className="h-3 w-3" /> Clear
        </button>
      )}

      <button
        type="button"
        disabled={createDisabled}
        onClick={onCreate}
        data-testid="open-create-dialog"
        className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-[#002B45] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#003a5d] disabled:opacity-50"
      >
        <Plus className="h-4 w-4" /> New
      </button>
    </div>
  );
};

export default FilesFilterBar;
